import WidgetHeader from '@/components/common/WidgetHeader';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import { GRID_KEYS } from '@/constants/gridKeys';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGridColumns } from '@/hooks/useGridColumns';

import {
  useGetHoldingUnrealizedGainsQuery,
  useGetUnrealizedGainsQuery,
  type PortfolioUnrealizedGainsQueryParams,
  type UnrealizedGainsQueryParams,
  type UnrealizedGainsRecord,
} from '@/store/holdingsApi';
import type { RecordResponse } from '@/models/ServiceTypes';
import useAccountDetailsFlyout from '@/hooks/useAccountDetailsFlyout';
import useHoldingDetailsFlyout from '@/hooks/useHoldingDetailsFlyout';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  DataGridPremium,
  type GridApiPremium,
  type GridGroupNode,
  GridCellParams,
  GridColDef,
  type GridListViewColDef,
  type GridRenderCellParams,
  useGridApiRef,
  GridRowId,
  useKeepGroupedColumnsHidden,
  GRID_AGGREGATION_FUNCTIONS,
  isAutogeneratedRow,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { skipToken, type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import {
  GainLossGroupByMode,
  getGainLossFilterQueryParams,
  getGainLossGroupingModel,
  formatTaxLotNumber,
  getHoldingRowDateFields,
  getTaxLotNumberSortComparator,
  shouldShowTaxLotDetailPanel,
  withTaxLotNumberColumn,
} from '@/modules/portfolio/gainLoss/constants';
import { useTickerUrlFormatter } from '@/hooks/useTickerUrlFormatter';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { TaxLotsDetailGrid } from './TaxLotsDetailGrid';
import { ExportButton } from '../../../components/grids/ExportButton';
import { unrealizedPercentChangeAggregation } from './aggregators/unrealizedPercentChangeAggregation';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { formatPercentValue } from '@/utils/numbers';
import GainsLossesUtility from './utilities/gains-losses.utility';
import { GridContainer } from '../../../components/grids/GridContainer';

export interface UnrealizedGainLossTabProps {
  holdingId?: number;
  accountId?: number;
  isWidget?: boolean;
  investorId?: number;
  groupBy?: GainLossGroupByMode;
}

/** Stable empty Set for detail panel expanded IDs (same pattern as EMPTY_* in selectors: avoid allocations, stable reference). */
const EMPTY_EXPANDED_ROW_IDS = new Set<GridRowId>();

/**
 * Shape of a DataGrid row built from an `UnrealizedGainsRecord`. The grid's
 * `valueGetter`/`renderCell` callbacks hand back rows as `GridValidRowModel`
 * (effectively `any`), so we cast to this when reading row fields to keep the
 * access type-safe without widening.
 */
export interface UnrealizedGainLossRow {
  id: number;
  WealthHoldingId: number;
  WealthAccountId: number;
  NumberOfTaxLots: number;
  TaxLotNumber: string;
  HoldingName: string;
  Symbol: string;
  CUSIP: string;
  Quantity: number;
  Price: number;
  OpenDate: string | undefined;
  AcquisitionDate: string | undefined;
  CostBasis: number;
  CurrentBalance: number;
  MarketValue: number;
  DollarChange: number;
  PercentChange: number;
  TaxLotTermDisplay: string;
  AccountName: string;
  GainTypeDisplay: string;
  DiversificationName: string;
}

const UnrealizedGainLossTab = ({
  holdingId,
  accountId,
  isWidget = false,
  investorId,
  groupBy = GainLossGroupByMode.NoGroup,
}: UnrealizedGainLossTabProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const authLoaded = useAuthIsLoaded();
  const apiRef = useGridApiRef();

  const { t } = useLocalization();
  const multipleLabel = t('gainsLosses.multiple');

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
  const { tickerUrlFormatter } = useTickerUrlFormatter();
  const effectiveExclusions = useEffectiveExclusions();
  // EUD-440: clicking an account name opens the Account Details flyout.
  const {
    openAccountById,
    openAccountByName,
    canOpenAccount,
    canOpenAccountByName,
    accountFlyout,
  } = useAccountDetailsFlyout({ investorId });
  const { openHolding, canOpenHolding, holdingFlyout } =
    useHoldingDetailsFlyout();

  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    Set<GridRowId>
  >(() => EMPTY_EXPANDED_ROW_IDS);

  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (newIds: Set<GridRowId> | GridRowId[]) => {
      setDetailPanelExpandedRowIds(
        newIds instanceof Set ? newIds : new Set(newIds),
      );
    },
    [],
  );

  // Reset expansion when holdingId changes
  useEffect(() => {
    setDetailPanelExpandedRowIds(EMPTY_EXPANDED_ROW_IDS);
  }, [holdingId]);

  // Fetch grid columns configuration from API (matching legacy behavior)
  // CRITICAL BUG REPLICATION: Legacy UnrealizedGainsGrid incorrectly uses
  // "investor-realized-gains" grid key when in single holding view instead of
  // "investor-unrealized-gains". This causes both tabs to share the same column
  // configuration when viewing a single holding. We replicate this exact behavior.
  const gridKey = holdingId
    ? GRID_KEYS.INVESTOR_REALIZED_GAINS
    : GRID_KEYS.INVESTOR_UNREALIZED_GAINS;

  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useGridColumns(gridKey);

  const groupingModel = useMemo(
    () => getGainLossGroupingModel(groupBy),
    [groupBy],
  );

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    rowGroupingModel: groupingModel,
    initialState: {
      columns: {
        columnVisibilityModel: {
          GainTypeDisplay: false,
          AccountName: false,
        },
      },
      rowGrouping: { model: groupingModel },
      aggregation: {
        model: {
          CostBasis: 'sum',
          CurrentBalance: 'sum',
          DollarChange: 'sum',
          PercentChange: 'unrealizedPercentChangeAggregation',
        },
      },
    },
  });

  // Get unrealized gains data
  const {
    data: portfolioData,
    isFetching: portfolioFetching,
    error: portfolioError,
  } = useGetUnrealizedGainsQuery(
    authLoaded && !holdingId
      ? ({
          // limit: 100,
          sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
          AccountId: accountId,
          ...getGainLossFilterQueryParams(
            investorId,
            effectiveExclusions,
            groupBy,
          ),
        } satisfies PortfolioUnrealizedGainsQueryParams)
      : skipToken,
  );

  const {
    data: holdingData,
    isFetching: holdingFetching,
    error: holdingError,
  } = useGetHoldingUnrealizedGainsQuery(
    authLoaded && holdingId
      ? ({
          holdingId,
          sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
          investorId,
        } satisfies UnrealizedGainsQueryParams)
      : skipToken,
  );

  // `holdingData`/`portfolioData` are loosely typed (any) at this call site
  // because the gains query funcs return `as any` on error; cast to the real
  // response shape so each `record` is a typed `UnrealizedGainsRecord`.
  const data = (holdingId ? holdingData : portfolioData) as
    | RecordResponse<UnrealizedGainsRecord>
    | undefined;
  // Same `any` degradation as `data`; the RTK result fields are loosely typed
  // here, so narrow them to their real shapes.
  const isLoading = Boolean(holdingId ? holdingFetching : portfolioFetching);
  const error = (holdingId ? holdingError : portfolioError) as
    | FetchBaseQueryError
    | SerializedError
    | undefined;

  // Transform data for DataGrid.
  const rows: UnrealizedGainLossRow[] = data?.Records
    ? data.Records.map((record, index) => {
        const dateFields = getHoldingRowDateFields(record, holdingId);
        return {
          id: index, // Use index for unique id
          WealthHoldingId: record.WealthHoldingId,
          WealthAccountId: record.WealthAccountId, // EUD-440: carry account id for the flyout
          NumberOfTaxLots: record.NumberOfTaxLots,
          TaxLotNumber: holdingId
            ? formatTaxLotNumber(record.TaxLotNumber)
            : '',
          HoldingName: record.HoldingName,
          Symbol: record.Symbol || '', // Ticker Symbol
          CUSIP: record.CUSIP || '',
          Quantity: record.Quantity,
          Price: record.Price,
          OpenDate: dateFields.OpenDate,
          AcquisitionDate: dateFields.OpenDate, // Map OpenDate to AcquisitionDate for columns that expect this field name
          CostBasis: record.CostBasis,
          CurrentBalance: record.CurrentBalance,
          MarketValue: record.CurrentBalance, // Map CurrentBalance to MarketValue for columns that expect this field name
          DollarChange: record.DollarChange,
          PercentChange: record.PercentChange,
          TaxLotTermDisplay: record.TaxLotTermDisplay,
          AccountName: record.AccountName,
          GainTypeDisplay: record.GainTypeDisplay,
          DiversificationName: record.DiversificationName ?? 'Unclassified',
        };
      })
    : [];

  // Create columns configuration based on API response
  const columns: GridColDef<UnrealizedGainLossRow>[] = useMemo(() => {
    // Default column definitions with full configuration
    // Note: Field names must match the GridColumnDataField from the API
    const defaultColumns: Record<string, GridColDef<UnrealizedGainLossRow>> = {
      TaxLotNumber: {
        field: 'TaxLotNumber',
        headerName: t('grid.number'),
        flex: 1,
        getSortComparator: getTaxLotNumberSortComparator,
      },
      HoldingName: {
        field: 'HoldingName',
        headerName: t('grid.holding'),
        flex: 2,
        renderCell: (params: GridRenderCellParams<UnrealizedGainLossRow>) => {
          if (params.rowNode.type === 'group') {
            if (params.rowNode.groupingField !== 'HoldingName') return null;
            const key = params.rowNode.groupingKey;
            if (key == null) return null;
            return <Typography variant='body2'>{String(key)}</Typography>;
          }
          const cellRow = params.row as Partial<UnrealizedGainLossRow>;
          const name = cellRow.HoldingName ?? '';
          if (!name) return null;
          const holdingId = cellRow.WealthHoldingId;
          if (!canOpenHolding(holdingId)) {
            return <Typography variant='body2'>{name}</Typography>;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => {
                openHolding({
                  holdingId,
                  name,
                  accountName: cellRow.AccountName,
                });
              }}
            >
              <Typography
                variant='body2'
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      Symbol: {
        field: 'Symbol',
        headerName: t('grid.symbol'),
        flex: 1,
        renderCell: (params) => {
          return tickerUrlFormatter(params.value);
        },
      },
      CUSIP: {
        field: 'CUSIP',
        headerName: t('grid.cusip'),
        flex: 1,
      },
      Quantity: {
        field: 'Quantity',
        headerName: t('grid.quantity'),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) => {
          // Grouping/autogenerated rows can hand back a nullish value even though
          // the row type says `number`; keep the runtime guard by widening.
          // EUD-504: format with full fractional precision.
          const value = params as number | null | undefined;
          return typeof value === 'number'
            ? value.toLocaleString('en-US', { maximumFractionDigits: 20 })
            : '';
        },
      },
      CostBasis: {
        field: 'CostBasis',
        headerName: t('grid.costBasis'),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params: number) => formatNumberToCurrency(params),
      },
      Price: {
        field: 'Price',
        headerName: t('grid.price'),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => {
          if (isAutogeneratedRow(row) || row.NumberOfTaxLots > 1) {
            return '';
          }

          return formatNumberToCurrency(value);
        },
      },
      OpenDate: GainsLossesUtility.createDateColumn({
        field: 'OpenDate',
        headerName: t('grid.openDate'),
        multipleLabel,
      }) as GridColDef<UnrealizedGainLossRow>,
      CurrentBalance: {
        field: 'CurrentBalance',
        headerName: t('grid.currentBalance'),
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) => formatNumberToCurrency(params),
      },
      DollarChange: {
        field: 'DollarChange',
        headerName: t('grid.dollarChange'),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) => formatNumberToCurrency(params),
        cellClassName: (params: GridCellParams) => {
          return (params.value as number) >= 0
            ? 'positive-value'
            : 'negative-value';
        },
      },
      PercentChange: {
        field: 'PercentChange',
        headerName: t('grid.percentChange'),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) => formatPercentValue(params),
        cellClassName: (params: GridCellParams) => {
          return (params.value as number) >= 0
            ? 'positive-value'
            : 'negative-value';
        },
      },
      TaxLotTermDisplay: {
        field: 'TaxLotTermDisplay',
        headerName: t('grid.term'),
        flex: 1,
      },
      // Add potential alternate field names from the API
      MarketValue: {
        field: 'CurrentBalance', // The API config uses CurrentBalance field but with MarketValue label
        headerName: t('grid.marketValue'),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) => formatNumberToCurrency(params),
      },
      AcquisitionDate: GainsLossesUtility.createDateColumn({
        field: 'OpenDate',
        headerName: t('grid.acquisitionDate'),
        multipleLabel,
      }) as GridColDef<UnrealizedGainLossRow>,
      AccountName: {
        field: 'AccountName',
        headerName: t('grid.account'),
        // EUD-440: clicking the account name opens the Account Details flyout.
        renderCell: (params: GridRenderCellParams<UnrealizedGainLossRow>) => {
          // When grouping by account, the grid delegates the group-header value to
          // this renderCell; group nodes have no AccountName field, so render the
          // grouping key (the account name) and open the flyout by name.
          if (params.rowNode.type === 'group') {
            // This is the AccountName column; it only has a value for account
            // groups. For any other grouping field the group row's cell here is
            // empty so it doesn't echo the other group's key (EUD-440).
            if (params.rowNode.groupingField !== 'AccountName') {
              return null;
            }
            const key = params.rowNode.groupingKey;
            if (key == null) return null;
            const groupName = String(key);
            if (!canOpenAccountByName(groupName)) {
              return <Typography variant='body2'>{groupName}</Typography>;
            }
            return (
              <Typography
                variant='body2'
                onClick={(event) => {
                  event.stopPropagation();
                  openAccountByName(groupName);
                }}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                {groupName}
              </Typography>
            );
          }
          // Grouping/autogenerated rows can omit these fields; read as partial
          // so the nullish guards stay honest.
          const cellRow = params.row as Partial<UnrealizedGainLossRow>;
          const name = cellRow.AccountName ?? '';
          if (!name) return null;
          const accountId = cellRow.WealthAccountId;
          if (!canOpenAccount(accountId)) {
            return <Typography variant='body2'>{name}</Typography>;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => {
                openAccountById(accountId);
              }}
            >
              <Typography
                variant='body2'
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      GainTypeDisplay: {
        field: 'GainTypeDisplay',
        headerName: t('grid.gainOrLoss'),
        flex: 1,
        valueGetter: (_value, row) => {
          if (isAutogeneratedRow(row)) {
            return '';
          }

          return row.DollarChange >= 0 ? t('gain') : t('loss');
        },
      },
      // Add DiversificationName column for asset allocation grouping
      DiversificationName: {
        field: 'DiversificationName',
        headerName: t('grid.assetAllocation', 'Asset Allocation'),
        flex: 1,
      },
    };

    // If we have grid columns configuration from the API, use it
    if (gridColumnsData?.Records && gridColumnsData.Records.length > 0) {
      console.log(
        'UNREALIZED GAINS - Grid columns from API:',
        gridColumnsData.Records,
      );

      const enabledColumns = gridColumnsData.Records.filter(
        (col) => col.IsEnabled,
      )
        .sort((a, b) => a.Order - b.Order)
        .map((col) => {
          console.log(
            'Processing column:',
            col.GridColumnDataField,
            'Label:',
            col.Label,
          );
          // Index access can miss (API field not in defaultColumns); keep the
          // runtime guard by typing the lookup as possibly-undefined.
          const baseColumn = defaultColumns[col.GridColumnDataField] as
            | GridColDef<UnrealizedGainLossRow>
            | undefined;
          if (baseColumn) {
            return {
              ...baseColumn,
              headerName: col.Label, // Use the label from API
            };
          }

          // Create a dynamic column for unknown fields
          console.warn('Creating dynamic column for:', col.GridColumnDataField);
          return {
            field: col.GridColumnDataField,
            headerName: col.Label,
            flex: 1,
            align: 'left' as const,
            headerAlign: 'left' as const,
            sortable: true,
          } as GridColDef;
        })
        .filter(Boolean) as GridColDef[];

      // console.log('UNREALIZED GAINS - Final enabled columns:', enabledColumns);

      // Always ensure required grouping columns are available (even if not explicitly configured)
      const requiredGroupingColumns = [
        defaultColumns.AccountName,
        defaultColumns.GainTypeDisplay,
      ].filter(
        (col) =>
          !enabledColumns.some((enabledCol) => enabledCol.field === col.field),
      );

      // Legacy parity: ensure Open Date is available by default.
      // Some column configs label it as "Acquisition Date" but it still maps to field "OpenDate".
      const requiredDisplayColumns = [defaultColumns.OpenDate].filter(
        (col) =>
          !enabledColumns.some((enabledCol) => enabledCol.field === col.field),
      );

      // Only add DiversificationName column when grouping by asset allocation
      const classificationColumn =
        groupBy === GainLossGroupByMode.ByAssetAllocation
          ? [defaultColumns.DiversificationName]
          : [];

      const columnsWithGrouping = [
        ...enabledColumns,
        ...requiredGroupingColumns,
        ...requiredDisplayColumns,
        ...classificationColumn,
      ];

      // Special handling for single holding view - matches legacy behavior
      if (holdingId) {
        // Remove HoldingName and Symbol columns and add TaxLotNumber at the beginning
        const filteredColumns = columnsWithGrouping.filter(
          (col) => col.field !== 'HoldingName' && col.field !== 'Symbol',
        );
        // console.log(
        //   'UNREALIZED GAINS - Single holding filtered columns:',
        //   filteredColumns,
        // );

        // For single holding view, reorder columns to match legacy UI exactly:
        // #, Quantity, Cost Basis, $ Change, % Change, Term, Price, Open Date, Current Balance
        const reorderedColumns = [
          defaultColumns.TaxLotNumber,
          filteredColumns.find((col) => col.field === 'Quantity') ||
            defaultColumns.Quantity,
          filteredColumns.find((col) => col.field === 'CostBasis') ||
            defaultColumns.CostBasis,
          filteredColumns.find((col) => col.field === 'DollarChange') ||
            defaultColumns.DollarChange,
          filteredColumns.find((col) => col.field === 'PercentChange') ||
            defaultColumns.PercentChange,
          filteredColumns.find((col) => col.field === 'TaxLotTermDisplay') ||
            defaultColumns.TaxLotTermDisplay,
          // Add Price column - fallback to default if not in API config (since realized gains config doesn't have Price)
          filteredColumns.find((col) => col.field === 'Price') ||
            defaultColumns.Price,
          // Add Open Date column - fallback to default if not in API config
          filteredColumns.find(
            (col) =>
              col.field === 'OpenDate' ||
              col.headerName?.includes('Acquisition'),
          )
            ? {
                ...(filteredColumns.find(
                  (col) =>
                    col.field === 'OpenDate' ||
                    col.headerName?.includes('Acquisition'),
                ) ?? defaultColumns.OpenDate),
                headerName: 'Open Date',
              }
            : defaultColumns.OpenDate,
          // Add Current Balance column - fallback to default if not in API config (since realized gains config doesn't have CurrentBalance)
          filteredColumns.find(
            (col) =>
              col.field === 'CurrentBalance' ||
              col.headerName?.includes('Market'),
          )
            ? {
                ...(filteredColumns.find(
                  (col) =>
                    col.field === 'CurrentBalance' ||
                    col.headerName?.includes('Market'),
                ) ?? defaultColumns.CurrentBalance),
                headerName: 'Current Balance',
              }
            : defaultColumns.CurrentBalance,
        ].filter(Boolean) as GridColDef[];

        // console.log(
        //   'UNREALIZED GAINS - Reordered columns for single holding:',
        //   reorderedColumns,
        // );
        return reorderedColumns;
      }

      return columnsWithGrouping;
    }

    // Fallback: return default enabled columns matching legacy UI unrealized gains
    const baseColumns = [
      defaultColumns.HoldingName,
      defaultColumns.Symbol,
      defaultColumns.AccountName,
      defaultColumns.Quantity,
      defaultColumns.Price,
      defaultColumns.OpenDate,
      defaultColumns.CostBasis,
      defaultColumns.CurrentBalance,
      defaultColumns.DollarChange,
      defaultColumns.PercentChange,
      defaultColumns.TaxLotTermDisplay,
      defaultColumns.GainTypeDisplay,
      // Only include DiversificationName column when grouping by asset allocation
      ...(groupBy === GainLossGroupByMode.ByAssetAllocation
        ? [defaultColumns.DiversificationName]
        : []),
    ].filter(Boolean) as GridColDef[];

    // Special handling for single holding view in fallback
    if (holdingId) {
      // For single holding view, use specific columns in the correct order
      // Based on legacy UI: #, Quantity, Cost Basis, $ Change, % Change, Term, Price, Open Date
      return [
        defaultColumns.TaxLotNumber,
        defaultColumns.AccountName,
        defaultColumns.Quantity,
        defaultColumns.CostBasis,
        defaultColumns.DollarChange,
        defaultColumns.PercentChange,
        defaultColumns.TaxLotTermDisplay,
        defaultColumns.Price,
        defaultColumns.OpenDate,
        defaultColumns.GainTypeDisplay,
        // Only include DiversificationName column when grouping by asset allocation
        ...(groupBy === GainLossGroupByMode.ByAssetAllocation
          ? [defaultColumns.DiversificationName]
          : []),
      ].filter(Boolean) as GridColDef[];
    }

    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gridColumnsData?.Records,
    holdingId,
    t,
    groupBy,
    // Accounts load after first render; without these the desktop columns keep
    // the initial (empty-map) callbacks and render account names as plain text
    // even after the lookup resolves (EUD-440 review).
    canOpenAccount,
    openAccountById,
    canOpenAccountByName,
    openAccountByName,
    canOpenHolding,
    openHolding,
  ]);

  // Defensive: ensure we never pass undefined/null entries into the grid.
  // DataGrid internals expect every column to be a valid object and can crash
  // when encountering invalid entries (e.g. reading `filterable` of undefined).
  const safeColumns: GridColDef[] = useMemo(() => {
    // `columns` is typed non-null, but the upstream `.filter(Boolean) as ...`
    // casts can still let holes through at runtime; treat each entry as
    // possibly-undefined so the guards below stay honest.
    const loose = columns as Array<
      GridColDef<UnrealizedGainLossRow> | undefined
    >;
    return loose.filter(
      (c): c is GridColDef<UnrealizedGainLossRow> =>
        !!c && typeof c.field === 'string',
    );
  }, [columns]);

  const listViewColDef: GridListViewColDef = useMemo(
    () => ({
      field: 'listColumn',
      headerName: '',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const { rowNode, api } = params;
        // Grouping/autogenerated rows can omit these fields, so read the row as
        // a partial view to keep the nullish guards below honest.
        const row = params.row as Partial<UnrealizedGainLossRow>;

        // Index access yields `boolean | undefined` at runtime (a field absent
        // from the model is visible); type it honestly so the `!== false` guard
        // stays meaningful.
        const visibilityModel = api.state.columns
          .columnVisibilityModel as Record<string, boolean | undefined>;
        const isFieldVisible = (field: string) =>
          visibilityModel[field] !== false;

        // Group header row when grouping is enabled (matches Accounts list-view behavior)
        if (rowNode.type === 'group') {
          const isExpanded = rowNode.childrenExpanded;
          const groupKey = String(rowNode.groupingKey ?? params.value ?? '');

          const apiPremium = api as GridApiPremium;

          const descendantCount = (
            rowNode as GridGroupNode & { descendantCount?: number }
          ).descendantCount;

          const childrenIdsCount = (
            rowNode as GridGroupNode & { childrenIds?: (string | number)[] }
          ).childrenIds?.length;

          // Some grid modes / states don't populate `descendantCount` or `childrenIds`
          // on the rowNode we receive in renderCell. When available, ask the grid API
          // directly for the group children to get an accurate count.
          const apiChildrenCount =
            typeof apiPremium.getRowGroupChildren === 'function'
              ? apiPremium.getRowGroupChildren({
                  groupId: params.id,
                  applySorting: true,
                  applyFiltering: true,
                }).length
              : undefined;

          const groupCount =
            descendantCount ?? childrenIdsCount ?? apiChildrenCount ?? 0;

          const handleToggleExpand = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            (api as GridApiPremium).setRowChildrenExpansion(
              params.id,
              !isExpanded,
            );
          };

          return (
            <Box
              sx={{
                width: '100%',
                px: 0,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <IconButton
                size='small'
                onClick={handleToggleExpand}
                sx={{ p: 0 }}
                aria-label={
                  isExpanded
                    ? t('grid.collapseGroup', 'Collapse group')
                    : t('grid.expandGroup', 'Expand group')
                }
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize='small' />
                ) : (
                  <ChevronRightIcon fontSize='small' />
                )}
              </IconButton>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: '600 !important',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {rowNode.groupingField === 'AccountName' &&
                canOpenAccountByName(groupKey) ? (
                  <Box
                    component='span'
                    onClick={(event) => {
                      event.stopPropagation();
                      openAccountByName(groupKey);
                    }}
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {groupKey}
                  </Box>
                ) : (
                  groupKey
                )}
                {groupCount > 0 ? ` (${groupCount.toString()})` : ''}
              </Typography>
            </Box>
          );
        }

        const holdingName = (row.HoldingName ?? '').trim();
        const accountName = (row.AccountName ?? '').trim();
        // EUD-658: four-digit year on the mobile card to match Realized Gains.
        const openDateValue = row.OpenDate ? formatAppDate(row.OpenDate) : '';

        const term = (row.TaxLotTermDisplay ?? '').trim();
        const quantity = row.Quantity != null ? row.Quantity : null;
        const price = row.Price != null ? row.Price : null;

        const costBasis = row.CostBasis != null ? row.CostBasis : null;
        const currentBalance =
          row.CurrentBalance != null ? row.CurrentBalance : null;
        const dollarChange = row.DollarChange != null ? row.DollarChange : null;
        const percentChange =
          row.PercentChange != null ? row.PercentChange : null;

        const getChangeColor = (value: number | null) => {
          if (!value) return 'text.secondary';
          return value >= 0
            ? theme.palette.success.main
            : theme.palette.error.main;
        };

        const Line = (line: {
          left: string;
          right?: string;
          rightColor?: string;
          bold?: boolean;
        }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              variant='body2'
              sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
            >
              {line.left}
            </Typography>
            {line.right != null ? (
              <Typography
                variant='body2'
                sx={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                  color: line.rightColor ?? 'text.secondary',
                }}
              >
                {line.right}
              </Typography>
            ) : null}
          </Box>
        );

        const showHolding = isFieldVisible('HoldingName');
        const showCurrentBalance = isFieldVisible('CurrentBalance');
        const showOpenDate = isFieldVisible('OpenDate');
        const showTerm = isFieldVisible('TaxLotTermDisplay');
        const showQuantity = isFieldVisible('Quantity');
        const showPrice = isFieldVisible('Price');
        const showCostBasis = isFieldVisible('CostBasis');
        const showDollarChange = isFieldVisible('DollarChange');
        const showPercentChange = isFieldVisible('PercentChange');
        const showAccount = isFieldVisible('AccountName');

        const sharesAtPrice =
          quantity != null && price != null
            ? t('grid.sharesAtPrice', {
                quantity: quantity.toLocaleString('en-US', {
                  maximumFractionDigits: 20,
                }),
                price: formatNumberToCurrency(price),
              })
            : '';

        return (
          <Box
            sx={{
              width: '100%',
              px: 0,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {/* Line 1: Holding name (left) + current balance (right) */}
            {(showHolding || showCurrentBalance) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body1'
                  onClick={
                    showHolding && canOpenHolding(row.WealthHoldingId)
                      ? (event) => {
                          event.stopPropagation();
                          openHolding({
                            holdingId: row.WealthHoldingId,
                            name: holdingName,
                            accountName: row.AccountName,
                          });
                        }
                      : undefined
                  }
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    fontWeight: '600 !important',
                    color: 'text.primary',
                    ...(showHolding && canOpenHolding(row.WealthHoldingId)
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : {}),
                  }}
                >
                  {showHolding ? holdingName : t('grid.holding', 'Holding')}
                </Typography>
                {showCurrentBalance ? (
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: '600 !important',
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    {formatNumberToCurrency(currentBalance ?? 0)}
                  </Typography>
                ) : null}
              </Box>
            )}

            {/* Line 2: Open Date */}
            {showOpenDate && openDateValue ? (
              <Line
                left={t('grid.openDate', 'Open Date')}
                right={openDateValue}
              />
            ) : null}

            {/* Line 3: Term-<term> (left) + "<qty> shares at <price>" (right) */}
            {(showTerm || showQuantity || showPrice) &&
            (term || sharesAtPrice) ? (
              <Line
                left={
                  showTerm && term
                    ? t('grid.termLabel', {
                        term,
                        defaultValue: `${term}-term`,
                      })
                    : t('grid.term', 'Term')
                }
                right={sharesAtPrice || undefined}
              />
            ) : null}

            {/* Line 4: Cost Basis */}
            {showCostBasis ? (
              <Line
                left={t('grid.costBasis', 'Cost Basis')}
                right={
                  costBasis != null ? formatNumberToCurrency(costBasis) : '—'
                }
              />
            ) : null}

            {/* Line 5: $ Change */}
            {showDollarChange ? (
              <Line
                left={t('grid.dollarChange', '$ Change')}
                right={
                  dollarChange != null
                    ? formatNumberToCurrency(dollarChange)
                    : '—'
                }
                rightColor={getChangeColor(dollarChange)}
                bold={dollarChange != null && dollarChange !== 0}
              />
            ) : null}

            {/* Line 6: % Change */}
            {showPercentChange ? (
              <Line
                left={t('grid.percentChange', '% Change')}
                right={
                  percentChange != null
                    ? formatPercentValue(percentChange)
                    : '—'
                }
                rightColor={getChangeColor(percentChange)}
                bold={percentChange != null && percentChange !== 0}
              />
            ) : null}

            {/* Line 7: Account Name (EUD-440: clickable -> Account Details flyout) */}
            {showAccount && accountName ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body1'
                  onClick={
                    canOpenAccount(row.WealthAccountId)
                      ? (event) => {
                          event.stopPropagation();
                          openAccountById(row.WealthAccountId);
                        }
                      : undefined
                  }
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: 'text.secondary',
                    ...(canOpenAccount(row.WealthAccountId)
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : {}),
                  }}
                >
                  {accountName}
                </Typography>
              </Box>
            ) : null}
          </Box>
        );
      },
    }),
    [
      t,
      theme.palette.error.main,
      theme.palette.success.main,
      canOpenAccount,
      openAccountById,
      canOpenAccountByName,
      openAccountByName,
      canOpenHolding,
      openHolding,
    ],
  );

  // Hide "Detail panel toggle"
  // https://github.com/mui/mui-x/issues/9968
  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => column.field !== GRID_DETAIL_PANEL_TOGGLE_FIELD)
      .map((column) => column.field);
  };

  // Hide export in the Holding Details flyout (holdingId set): its rows are
  // already scoped "By Tax Lots" for that holding, and export always hits the
  // portfolio-wide endpoint, so it would download the wrong data.
  const canExport = !isNative && !isMobile && !holdingId;

  return (
    <Stack
      direction='column'
      gap={3}
    >
      <WidgetHeader title={holdingId ? t('byTaxLots') : t('byHoldings')} />

      {error ? (
        <Typography color='error'>
          {t('errorLoadingData')}: {JSON.stringify(error)}
        </Typography>
      ) : (
        <Box
          sx={{
            width: '100%',
          }}
        >
          {canExport && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <ExportButton
                columns={withTaxLotNumberColumn(
                  safeColumns,
                  t('grid.lotNumber'),
                )}
                disabled={isLoading}
                label={t('grid.exportWithTaxLots')}
                fileName='UnrealizedGainsWithTaxLots'
                childProperty='TaxLots'
                dataUrl='unrealizedGains'
                investorId={investorId}
                queryParams={{
                  ...getGainLossFilterQueryParams(
                    investorId,
                    effectiveExclusions,
                    groupBy,
                  ),
                  sort: [
                    { Property: 'PercentChange', Direction: 'desc', Order: 1 },
                  ],
                }}
              />
            </Box>
          )}
          <GridContainer isWidget={isWidget}>
            <DataGridPremium
              rows={rows}
              columns={safeColumns}
              apiRef={apiRef}
              rowGroupingModel={groupingModel}
              initialState={initialState}
              aggregationFunctions={{
                ...GRID_AGGREGATION_FUNCTIONS,
                unrealizedPercentChangeAggregation, // custom aggregations MUST be added here.
              }}
              defaultGroupingExpansionDepth={-1}
              disableRowSelectionOnClick
              disableColumnFilter
              showToolbar
              disablePivoting
              getDetailPanelContent={({ row }) => {
                const typedRow = row as Partial<UnrealizedGainLossRow>;
                if (!shouldShowTaxLotDetailPanel(typedRow, holdingId))
                  return null;
                // shouldShowTaxLotDetailPanel guarantees a non-null id here.
                if (typedRow.WealthHoldingId == null) return null;
                return (
                  <TaxLotsDetailGrid
                    realized={false}
                    holdingId={typedRow.WealthHoldingId}
                    investorId={investorId}
                    parentColumns={safeColumns}
                  />
                );
              }}
              getDetailPanelHeight={() => 'auto'}
              detailPanelExpandedRowIds={detailPanelExpandedRowIds}
              onDetailPanelExpandedRowIdsChange={
                handleDetailPanelExpandedRowIdsChange
              }
              listView={isMobile}
              {...(isMobile ? { listViewColumn: listViewColDef } : {})}
              {...(isMobile ? { getRowHeight: () => 'auto' as const } : {})}
              loading={isLoading || !authLoaded || gridColumnsLoading}
              slotProps={{
                loadingOverlay: {
                  variant: 'linear-progress',
                  noRowsVariant: 'skeleton',
                },
                toolbar: {
                  ...defaultToolbarExportSlotProps,
                  showQuickFilter: true,
                },
                columnsManagement: {
                  getTogglableColumns,
                },
              }}
              slots={{
                ...(isNative
                  ? {
                      toolbar: (
                        props: Parameters<typeof CustomToolbarForNative>[0],
                      ) => <CustomToolbarForNative {...props} />,
                    }
                  : isMobile
                    ? {
                        toolbar: (
                          props: Parameters<
                            typeof XsPreferencesDrawerToolbar
                          >[0],
                        ) => (
                          <XsPreferencesDrawerToolbar
                            {...props}
                            showFilters={false}
                          />
                        ),
                      }
                    : {}),
              }}
              sx={{
                '& .positive-value': {
                  color: theme.palette.success.main,
                  fontWeight: 600,
                },
                '& .negative-value': {
                  color: theme.palette.error.main,
                  fontWeight: 600,
                },
                // Hide aggregation label
                '& .MuiDataGrid-aggregationColumnHeaderLabel': {
                  display: 'none',
                },
              }}
            />
          </GridContainer>
        </Box>
      )}

      {/* EUD-440: Account Details flyout, opened by clicking an account name. */}
      {accountFlyout}
      {holdingFlyout}
    </Stack>
  );
};

export default UnrealizedGainLossTab;
